import React, { useState, useEffect } from 'react';
import { X, Folder, Trash2, Copy, Edit2, Download, Calendar, FileText, Clock } from 'lucide-react';
import { Project } from '../types';
import { listProjects, deleteProject, duplicateProject, renameProject, exportProjectAsJSON } from '../utils/projectStorage';

interface ProjectManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadProject: (project: Project) => void;
  onNewProject: () => void;
  currentProjectId?: string;
  onOpenVersionHistory?: (project: Project) => void;
}

const ProjectManager: React.FC<ProjectManagerProps> = ({
  isOpen,
  onClose,
  onLoadProject,
  onNewProject,
  currentProjectId,
  onOpenVersionHistory
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    if (isOpen) {
      setProjects(listProjects().sort((a, b) => 
        new Date(b.modified).getTime() - new Date(a.modified).getTime()
      ));
    }
  }, [isOpen]);

  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this project?')) {
      deleteProject(id);
      setProjects(projects.filter(p => p.id !== id));
    }
  };

  const handleDuplicate = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newProject = duplicateProject(id);
    if (newProject) {
      setProjects([newProject, ...projects]);
    }
  };

  const handleRename = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const project = projects.find(p => p.id === id);
    if (project) {
      setEditingId(id);
      setEditName(project.name);
    }
  };

  const handleSaveRename = (id: string) => {
    if (editName.trim()) {
      renameProject(id, editName.trim());
      setProjects(projects.map(p => 
        p.id === id ? { ...p, name: editName.trim(), modified: new Date().toISOString() } : p
      ));
    }
    setEditingId(null);
  };

  const handleExport = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    const json = exportProjectAsJSON(project);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project.name}.archweaver.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200]" onClick={onClose}>
      <div 
        className="bg-white rounded-lg shadow-2xl w-[800px] max-w-[90vw] max-h-[80vh] flex flex-col border-2 border-black"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-black/10 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Folder size={24} className="text-black" />
            <h2 className="text-xl font-bold text-black">Projects</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-black transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* New Project Button */}
        <div className="p-6 border-b border-black/10">
          <button
            onClick={() => {
              onNewProject();
              onClose();
            }}
            className="w-full bg-black hover:bg-gray-800 text-white px-4 py-3 rounded font-medium transition-all shadow-[3px_3px_0px_0px_rgba(0,0,0,0.2)] active:shadow-none active:translate-x-[1px] active:translate-y-[1px]"
          >
            + New Project
          </button>
        </div>

        {/* Project List */}
        <div className="flex-1 overflow-y-auto p-6">
          {projects.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Folder size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-lg">No projects yet</p>
              <p className="text-sm mt-2">Create your first architecture diagram</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {projects.map((project) => (
                <div
                  key={project.id}
                  onClick={() => {
                    onLoadProject(project);
                    onClose();
                  }}
                  className={`
                    border-2 rounded-lg p-4 cursor-pointer transition-all
                    ${project.id === currentProjectId 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-400 hover:shadow-md'
                    }
                  `}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      {editingId === project.id ? (
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onBlur={() => handleSaveRename(project.id)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') handleSaveRename(project.id);
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                          className="text-lg font-semibold text-black border-2 border-blue-500 rounded px-2 py-1 w-full"
                        />
                      ) : (
                        <h3 className="text-lg font-semibold text-black truncate">
                          {project.name}
                        </h3>
                      )}
                      
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {formatDate(project.modified)}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText size={14} />
                          {project.nodes.length} components
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={(e) => handleRename(project.id, e)}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Rename"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={(e) => handleDuplicate(project.id, e)}
                        className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                        title="Duplicate"
                      >
                        <Copy size={16} />
                      </button>
                      <button
                        onClick={(e) => handleExport(project, e)}
                        className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                        title="Export JSON"
                      >
                        <Download size={16} />
                      </button>
                      {onOpenVersionHistory && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenVersionHistory(project);
                          }}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Version History"
                        >
                          <Clock size={16} />
                        </button>
                      )}
                      <button
                        onClick={(e) => handleDelete(project.id, e)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Thumbnail Preview */}
                  {project.thumbnail && (
                    <div className="mt-3 border border-gray-200 rounded overflow-hidden bg-gray-50">
                      <img 
                        src={project.thumbnail} 
                        alt={`${project.name} preview`}
                        className="w-full h-32 object-contain"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer Info */}
        <div className="p-4 border-t border-black/10 bg-gray-50 text-xs text-gray-500 text-center">
          {projects.length} project{projects.length !== 1 ? 's' : ''} • Stored locally in browser
        </div>
      </div>
    </div>
  );
};

export default ProjectManager;
